import type {
    DomainStatItem,
    DomainStatPriorSourceReference,
    DomainStatSourceItem,
    DomainStatSourceReference,
    DomainStatsResponse,
    OverviewTimeseriesResponse,
    TimeseriesDataPoint,
    TimeseriesSourceItem,
    TimeseriesSourceMetrics,
    TimeseriesSourceReference,
} from '../types/reporting';

export type {
    DomainStatPriorSourceReference,
    DomainStatSourceItem,
    DomainStatSourceReference,
    TimeseriesSourceReference,
} from '../types/reporting';

function isInheritedSource(entry: DomainStatSourceItem): entry is DomainStatSourceReference {
    return (entry as DomainStatSourceReference).metricsInheritedFromParent === true;
}

function isPriorInheritedSource(entry: DomainStatSourceItem): entry is DomainStatPriorSourceReference {
    return (entry as DomainStatPriorSourceReference).priorInheritedFromParent === true;
}

function isInheritedTimeseriesSource(
    entry: TimeseriesSourceItem,
): entry is TimeseriesSourceReference {
    return (entry as TimeseriesSourceReference).metricsInheritedFromParent === true;
}

function zeroNumericFields<T extends Record<string, unknown>>(template: T): T {
    const result: Record<string, unknown> = { ...template };
    for (const key of Object.keys(result)) {
        const value = result[key];
        if (typeof value === 'number') result[key] = 0;
        else if (Array.isArray(value)) result[key] = [];
    }
    return result as T;
}

/**
 * Expand inherited markers back into full metric objects so marketplace charts,
 * tables and CSV export can read every source entry the same way.
 *
 * - `metricsInheritedFromParent`: current ≡ parent. Prior/change only when this
 *   is the sole bySource entry (otherwise a sibling owns parent prior).
 * - `priorInheritedFromParent`: current = 0, prior ≡ parent.prior.
 */
export function rehydrateDomainStatItem<T extends DomainStatItem>(item: T): T {
    const bySource = item.bySource;
    if (!bySource) return item;

    const entries = Object.entries(bySource);
    const needsRehydrate = entries.some(
        ([, entry]) => isInheritedSource(entry) || isPriorInheritedSource(entry),
    );
    if (!needsRehydrate) return item;

    const inheritParentComparison = entries.length === 1 && isInheritedSource(entries[0][1]);
    const {
        bySource: _nested,
        id: _id,
        name: _name,
        prior: parentPrior,
        change: parentChange,
        ...parentCurrentMetrics
    } = item;

    const expanded: Record<string, DomainStatItem> = {};
    for (const [sourceKey, entry] of entries) {
        if (isPriorInheritedSource(entry)) {
            const zeroCurrent = zeroNumericFields({
                ...parentCurrentMetrics,
                id: entry.id,
                name: entry.name,
            }) as DomainStatItem;
            expanded[sourceKey] = {
                ...zeroCurrent,
                prior: parentPrior,
            };
            continue;
        }

        if (isInheritedSource(entry)) {
            const base = {
                ...parentCurrentMetrics,
                id: entry.id,
                name: entry.name,
            } as DomainStatItem;
            if (inheritParentComparison) {
                expanded[sourceKey] = { ...base, prior: parentPrior, change: parentChange };
            } else {
                const zeroPrior = parentPrior
                    ? zeroNumericFields({ ...parentPrior })
                    : undefined;
                expanded[sourceKey] = zeroPrior
                    ? { ...base, prior: zeroPrior as DomainStatItem['prior'] }
                    : base;
            }
            continue;
        }

        expanded[sourceKey] = entry;
    }

    return { ...item, bySource: expanded };
}

export function rehydrateDomainStatsResponse(
    response: DomainStatsResponse,
): DomainStatsResponse {
    const items = response.items.map(rehydrateDomainStatItem);
    return items.every((item, index) => item === response.items[index])
        ? response
        : { ...response, items };
}

function parentTimeseriesMetrics(
    parent: Record<string, unknown>,
): TimeseriesSourceMetrics {
    const {
        bySource: _bySource,
        prior: _prior,
        change: _change,
        date: _date,
        priorDate: _priorDate,
        priorEndDate: _priorEndDate,
        ...metrics
    } = parent;
    return metrics as TimeseriesSourceMetrics;
}

function expandTimeseriesBySource(
    bySource: Record<string, TimeseriesSourceItem> | undefined,
    parentMetrics: TimeseriesSourceMetrics,
): Record<string, TimeseriesSourceMetrics> | undefined {
    if (!bySource) return undefined;
    const entries = Object.entries(bySource);
    if (!entries.some(([, entry]) => isInheritedTimeseriesSource(entry))) {
        return bySource as Record<string, TimeseriesSourceMetrics>;
    }

    const expanded: Record<string, TimeseriesSourceMetrics> = {};
    for (const [sourceKey, entry] of entries) {
        if (isInheritedTimeseriesSource(entry)) {
            expanded[sourceKey] = { ...parentMetrics };
            continue;
        }
        expanded[sourceKey] = entry;
    }
    return expanded;
}

/**
 * Expand sole-source `metricsInheritedFromParent` markers on overview timeseries
 * points (current + prior.bySource) so charts can read numeric metrics per source.
 */
export function rehydrateOverviewTimeseriesPoint<T extends TimeseriesDataPoint>(point: T): T {
    const currentBySource = point.bySource;
    const priorBySource = point.prior?.bySource as Record<string, TimeseriesSourceItem> | undefined;

    const needsCurrent = !!currentBySource
        && Object.values(currentBySource).some(isInheritedTimeseriesSource);
    const needsPrior = !!priorBySource
        && Object.values(priorBySource).some(isInheritedTimeseriesSource);
    if (!needsCurrent && !needsPrior) return point;

    const expandedCurrent = expandTimeseriesBySource(
        currentBySource,
        parentTimeseriesMetrics(point as unknown as Record<string, unknown>),
    );

    let prior = point.prior;
    if (needsPrior && prior) {
        const expandedPriorBySource = expandTimeseriesBySource(
            priorBySource,
            parentTimeseriesMetrics(prior as unknown as Record<string, unknown>),
        );
        prior = { ...prior, bySource: expandedPriorBySource };
    }

    return {
        ...point,
        ...(expandedCurrent ? { bySource: expandedCurrent } : {}),
        ...(prior !== point.prior ? { prior } : {}),
    };
}

export function rehydrateOverviewTimeseriesResponse(
    response: OverviewTimeseriesResponse,
): OverviewTimeseriesResponse {
    const data = response.data.map(rehydrateOverviewTimeseriesPoint);
    return data.every((point, index) => point === response.data[index])
        ? response
        : { ...response, data };
}
